import { PgnDataBuilder } from "./PgnDataBuilder";
import { generateScenarioFingerprint } from "./ScenarioFingerprint";
/**
 * Default metadata for scenarios without PGN or with parsing errors
 */
export const DEFAULT_METADATA = {
    previewFen: "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1",
    previewLastMove: null,
    moveCount: 0,
    hasValidationErrors: false,
    fingerprint: "",
};
/**
 * Extracts metadata from an already-computed PgnData object.
 *
 * @param pgnData The PgnData object (already computed via PgnDataBuilder)
 * @param pgn The original PGN string (needed for fingerprint computation)
 * @param chess Chess instance (needed for fingerprint computation)
 * @returns Extracted metadata
 */
export async function extractMetadataFromPgnData(pgnData, pgn, chess) {
    const previewMove = pgnData.previewMove;
    const moves = pgnData.moves || [];
    const lastMove = moves.length > 0 ? moves[moves.length - 1] : null;
    const moveCount = lastMove ? lastMove.number : 0;
    // Only consider actual invalid moves as errors, not move number mismatch warnings
    const errors = pgnData.validationErrors || [];
    const hasActualInvalidMoves = errors.some((err) => err.includes("Invalid chess move") || err.includes("malformed headers"));
    // Compute fingerprint
    const fingerprint = await generateScenarioFingerprint(pgn, chess);
    return {
        previewFen: pgnData.previewFen,
        previewLastMove: previewMove
            ? { from: previewMove.from, to: previewMove.to }
            : null,
        moveCount,
        hasValidationErrors: hasActualInvalidMoves,
        fingerprint,
    };
}
/**
 * Computes metadata from a PGN string using a provided Chess instance.
 * This is the single source of truth for metadata computation.
 *
 * @param pgn The PGN string to parse
 * @param chess A Chess instance to use for parsing (required to avoid import issues)
 * @returns Computed metadata, or DEFAULT_METADATA if parsing fails
 */
export async function computeMetadataFromPgn(pgn, chess) {
    if (!pgn || typeof pgn !== "string") {
        return DEFAULT_METADATA;
    }
    try {
        const builder = new PgnDataBuilder(pgn, chess);
        const pgnData = builder.pgnData();
        return await extractMetadataFromPgnData(pgnData, pgn, chess);
    }
    catch (error) {
        return {
            ...DEFAULT_METADATA,
            hasValidationErrors: true,
        };
    }
}
